import { Injectable, inject } from '@angular/core';
import { Observable, of, delay, map } from 'rxjs';
import { HttpClient, HttpParams, HttpEvent, HttpRequest, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../../../environments/environmet';
import { ApiResponse } from '../../../companies/data/models/company.models';

import { CompanyCropSelectionsResponse, JobEnhancementOptionsResponse, JobListResponse, RequeueFailedStageResponse, SubJobItem } from '../models/job.models';
import { CheckoutSessionResponse, DownloadCheckoutSessionResponse, JobFormResponse } from '../models/job-form.models';
import { JobPhotosResponse } from '../models/job-photo.models';
import { JobErrorLogsResponse } from '../models/job-error-log.models';
import {
  UploadJobImagesResponse,
  JobUploadSessionResponse,
  CompleteJobUploadSessionResponse,
  StartOrResumeUploadSessionRequest,
  PrepareFilesRequest,
  PrepareFilesResponse,
  ConfirmFileRequest,
  ConfirmFileResponse,
  PendingFilesResponse,
  LogUploadErrorRequest
} from '../models/job-image-upload.models';
import { CompanyEnhancementSettings } from '../models/preset.models';

@Injectable({
  providedIn: 'root',
})
export class JobsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiBaseUrl}/Jobs`;

  getJobs(companyId: number, pageNumber: number = 1, pageSize: number = 100): Observable<JobListResponse> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('companyId', companyId.toString());

    return this.http.get<ApiResponse<any>>(this.baseUrl, { params }).pipe(
      map(res => {
        if (res.data?.items !== undefined) {
           return {
             ...res.data,
             items: res.data.items.map((item: any) => ({
               ...item,
               id: item.jobId
             }))
           };
        }
        // Fallback in case the API returns just a list in data
        return {
          items: (res.data || []).map((item: any) => ({
            ...item,
            id: item.jobId || item.id
          })),
          totalCount: res.data?.length || 0,
          pageNumber: pageNumber,
          pageSize: pageSize,
          totalPages: 1
        };
      })
    );
  }

  getSubJobs(jobId: number): Observable<ApiResponse<SubJobItem[]>> {
    return this.http.get<ApiResponse<SubJobItem[]>>(`${this.baseUrl}/${jobId}/subjobs`);
  }

  getEnhancementOptions(companyId: number): Observable<JobEnhancementOptionsResponse> {
    const params = new HttpParams().set('companyId', companyId.toString());
    return this.http.get<JobEnhancementOptionsResponse>(`${this.baseUrl}/enhancement-options`, { params });
  }

  getPresetsByCategoryId(presetCategoryId: number): Observable<any> {
    return this.http.get<any>(`${environment.apiBaseUrl}/presets/category/${presetCategoryId}`);
  }

  getCropSelections(jobId: number, companyId: number): Observable<CompanyCropSelectionsResponse> {
    const params = new HttpParams().set('companyId', companyId.toString());
    return this.http.get<CompanyCropSelectionsResponse>(`${this.baseUrl}/${jobId}/crop-selections`, { params });
  }

  getCompanyEnhancementSettings(companyId: number): Observable<ApiResponse<CompanyEnhancementSettings>> {
    return this.http.get<ApiResponse<CompanyEnhancementSettings>>(
      `${this.baseUrl}/company/${companyId}/enhancement-settings`
    );
  }

  runEnhancementTest(companyId: number, presetName: string, file: File): Observable<ApiResponse<CompanyEnhancementSettings>> {
    const formData = new FormData();
    formData.append('companyId', companyId.toString());
    formData.append('presetName', presetName);
    formData.append('file', file, file.name);

    return this.http.post<ApiResponse<CompanyEnhancementSettings>>(
      `${this.baseUrl}/enhancement-test`,
      formData
    );
  }

  saveEnhancementPreset(jobId: number, presetName: string, companyId: number): Observable<{ success: boolean; message: string }> {
    const params = new HttpParams().set('companyId', companyId.toString());
    return this.http.put<{ success: boolean; message: string }>(
      `${this.baseUrl}/${jobId}/enhancement-preset`,
      { presetName },
      { params }
    );
  }

  saveCropSelections(jobId: number, selections: any[], companyId: number): Observable<{ success: boolean; message: string; data: any }> {
    const params = new HttpParams().set('companyId', companyId.toString());
    return this.http.post<{ success: boolean; message: string; data: any }>(
      `${this.baseUrl}/${jobId}/crop-selections`,
      { selections },
      { params }
    );
  }

  getJobById(jobId: number): Observable<JobFormResponse> {
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/${jobId}`).pipe(
      map(res => {
        const data = res.data || {};
        return {
          ...data,
          id: data.jobId,
          noOfImagesInThisJob: data.noOfImages ?? data.noOfImagesInThisJob,
          avgFramesPhotographedPerStudent: data.avgFramesPhotographedPerStudent ?? data.avgFramesPerStudent,
          desiredImagesPosesPerStudent: data.desiredImagesPosesPerStudent ?? data.desiredImagesPerStudent,
          pickingCharge: data.pickingCharge ?? data.PickingCharge ?? 0,
          cropCharge: data.cropCharge ?? data.CropCharge ?? 0,
          enhancementCharge: data.enhancementCharge ?? data.EnhancementCharge ?? 0,
          extractionCharge: data.extractionCharge ?? data.ExtractionCharge ?? 0,
          estimatedCostOfJob: data.estimatedCostOfJob ?? data.EstimatedCostOfJob ?? 0,
          upfrontCostOfJob: data.advanceAmount || data.upfrontCostOfJob || data.estimatedCostOfJob || data.EstimatedCostOfJob || 0,
          isPaid: data.paymentStatus === 'Paid' || data.paymentStatus === 'Completed'
        };
      })
    );
  }

  createJob(payload: any): Observable<{ success: boolean; id: number }> {
    return this.http.post<ApiResponse<any>>(this.baseUrl, payload).pipe(
      map(res => ({
        success: res.success,
        id: res.data?.jobId
      }))
    );
  }

  updateJob(jobId: number, payload: any): Observable<{ success: boolean }> {
    return this.http.put<ApiResponse<any>>(`${this.baseUrl}/${jobId}`, payload).pipe(
      map(res => ({
        success: res.success
      }))
    );
  }

  deleteJob(jobId: number): Observable<{ success: boolean }> {
    return this.http.delete<ApiResponse<any>>(`${this.baseUrl}/${jobId}`).pipe(
       map(res => ({
         success: res.success
       }))
    );
  }

  getJobPhotos(jobId: number): Observable<JobPhotosResponse> {
    // Keep mock for now since job photos API is not ready
    return of({
      jobId,
      jobName: 'Giacomo Guilizzoni',
      totalUploadedImages: 15,
      selectedImages: 2,
      cropImages: true,
      enhanceImages: true,
      items: Array.from({ length: 15 }).map((_, index) => ({
        id: `${jobId}-${index + 1}`,
        fileName: `photo-${index + 1}.jpg`,
        previewUrl: '',
        isSelected: index === 4 || index === 9,
      })),
    }).pipe(delay(300));
  }

  getActiveJobUploadSession(jobId: number, companyId: number): Observable<ApiResponse<JobUploadSessionResponse>> {
    const params = new HttpParams().set('companyId', companyId.toString());
    return this.http.get<ApiResponse<JobUploadSessionResponse>>(`${this.baseUrl}/${jobId}/active-upload-session`, {
      params,
      headers: new HttpHeaders({ 'X-Skip-Loading': 'true' })
    });
  }

  createJobUploadSession(jobId: number, companyId: number, expectedFileCount: number): Observable<ApiResponse<JobUploadSessionResponse>> {
    return this.http.post<ApiResponse<JobUploadSessionResponse>>(`${this.baseUrl}/${jobId}/upload-sessions`, { companyId, expectedFileCount });
  }

  getJobUploadSession(jobId: number, sessionId: number, companyId: number): Observable<ApiResponse<JobUploadSessionResponse>> {
    const params = new HttpParams().set('companyId', companyId.toString());
    return this.http.get<ApiResponse<JobUploadSessionResponse>>(`${this.baseUrl}/${jobId}/upload-sessions/${sessionId}`, {
      params,
      headers: new HttpHeaders({ 'X-Skip-Loading': 'true' })
    });
  }

  getJobUploadSessionItems(jobId: number, sessionId: number, companyId: number): Observable<ApiResponse<any>> {
    const params = new HttpParams().set('companyId', companyId.toString());
    return this.http.get<ApiResponse<any>>(`${this.baseUrl}/${jobId}/upload-session/${sessionId}/items`, {
      params,
      headers: new HttpHeaders({ 'X-Skip-Loading': 'true' })
    });
  }

  completeJobUploadSession(jobId: number, sessionId: number, companyId: number): Observable<ApiResponse<CompleteJobUploadSessionResponse>> {
    const params = new HttpParams().set('companyId', companyId.toString());
    return this.http.post<ApiResponse<CompleteJobUploadSessionResponse>>(`${this.baseUrl}/${jobId}/upload-sessions/${sessionId}/complete`, {}, { params });
  }

  /**
   * Starts a new upload session or resumes the latest in-progress one.
   * Use this instead of createJobUploadSession for the SAS direct-to-blob flow.
   */
  startOrResumeUploadSession(
    jobId: number,
    body: StartOrResumeUploadSessionRequest
  ): Observable<ApiResponse<JobUploadSessionResponse>> {
    return this.http.post<ApiResponse<JobUploadSessionResponse>>(
      `${this.baseUrl}/${jobId}/upload-sessions/start-or-resume`,
      body,
      { headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }) }
    );
  }

  /**
   * Registers a batch of files and returns SAS write URLs.
   * Items with alreadyUploaded=true need no further upload.
   */
  prepareFiles(
    jobId: number,
    sessionId: number,
    body: PrepareFilesRequest
  ): Observable<ApiResponse<PrepareFilesResponse>> {
    return this.http.post<ApiResponse<PrepareFilesResponse>>(
      `${this.baseUrl}/${jobId}/upload-sessions/${sessionId}/prepare-files`,
      body,
      { headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }) }
    );
  }

  /**
   * Notifies the API that a file was successfully uploaded to Azure Blob.
   * Creates the JobImage record and queues thumbnail generation.
   */
  confirmFile(
    jobId: number,
    sessionId: number,
    body: ConfirmFileRequest
  ): Observable<ApiResponse<ConfirmFileResponse>> {
    return this.http.post<ApiResponse<ConfirmFileResponse>>(
      `${this.baseUrl}/${jobId}/upload-sessions/${sessionId}/confirm-file`,
      body,
      { headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }) }
    );
  }

  /**
   * Returns all session items that are still Pending or Failed.
   * Use on resume to identify what still needs uploading.
   */
  getPendingFiles(
    jobId: number,
    sessionId: number
  ): Observable<ApiResponse<PendingFilesResponse>> {
    return this.http.get<ApiResponse<PendingFilesResponse>>(
      `${this.baseUrl}/${jobId}/upload-sessions/${sessionId}/pending-files`,
      { headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }) }
    );
  }

  /**
   * Refreshes SAS write URLs for all pending items.
   * Call when uploadUrlExpiresAt has passed.
   */
  refreshUploadUrls(
    jobId: number,
    sessionId: number
  ): Observable<ApiResponse<PendingFilesResponse>> {
    return this.http.post<ApiResponse<PendingFilesResponse>>(
      `${this.baseUrl}/${jobId}/upload-sessions/${sessionId}/refresh-upload-urls`,
      {},
      { headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }) }
    );
  }

  logUploadError(
    jobId: number,
    sessionId: number,
    body: LogUploadErrorRequest
  ): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.baseUrl}/${jobId}/upload-sessions/${sessionId}/log-upload-error`,
      body,
      { headers: new HttpHeaders({ 'X-Skip-Loading': 'true' }) }
    );
  }

  getErrorLogs(params: {
    jobId?: number | null;
    jobImageId?: number | null;
    processType?: string | null;
    stage?: string | null;
    status?: string | null;
    errorCode?: string | null;
    sourceWorker?: string | null;
    isResolved?: boolean | null;
    pageNumber: number;
    pageSize: number;
  }): Observable<ApiResponse<JobErrorLogsResponse>> {
    let httpParams = new HttpParams()
      .set('pageNumber', params.pageNumber.toString())
      .set('pageSize', params.pageSize.toString());

    if (params.jobId !== undefined && params.jobId !== null) {
      httpParams = httpParams.set('jobId', params.jobId.toString());
    }
    if (params.jobImageId !== undefined && params.jobImageId !== null) {
      httpParams = httpParams.set('jobImageId', params.jobImageId.toString());
    }
    if (params.processType) {
      httpParams = httpParams.set('processType', params.processType);
    }
    if (params.stage) {
      httpParams = httpParams.set('stage', params.stage);
    }
    if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }
    if (params.errorCode) {
      httpParams = httpParams.set('errorCode', params.errorCode);
    }
    if (params.sourceWorker) {
      httpParams = httpParams.set('sourceWorker', params.sourceWorker);
    }
    if (params.isResolved !== undefined && params.isResolved !== null) {
      httpParams = httpParams.set('isResolved', params.isResolved.toString());
    }

    return this.http.get<ApiResponse<JobErrorLogsResponse>>(`${this.baseUrl}/error-logs`, { params: httpParams });
  }

  uploadJobImages(jobId: number, companyId: number, jobUploadSessionId: number, files: File[]): Observable<HttpEvent<ApiResponse<UploadJobImagesResponse>>> {
    const formData = new FormData();
    formData.append('jobUploadSessionId', jobUploadSessionId.toString());
    files.forEach(file => {
      formData.append('files', file, file.name);
    });

    const params = new HttpParams().set('companyId', companyId.toString());
    const req = new HttpRequest('POST', `${this.baseUrl}/${jobId}/images`, formData, {
      params,
      reportProgress: true,
      responseType: 'json',
      headers: new HttpHeaders({ 'X-Skip-Loading': 'true' })
    });

    return this.http.request<ApiResponse<UploadJobImagesResponse>>(req);
  }


  submitJobProcessing(jobId: number): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/${jobId}/submit-processing`, {});
  }

  requeueFailedStage(jobId: number, stage: string): Observable<ApiResponse<RequeueFailedStageResponse>> {
    return this.http.post<ApiResponse<RequeueFailedStageResponse>>(`${this.baseUrl}/${jobId}/requeue-failed-stage`, { stage });
  }

  createCheckoutSession(jobId: number, amount: number, successUrl: string, cancelUrl: string): Observable<CheckoutSessionResponse> {
    const payload = {
      jobId,
      amount,
      successUrl,
      cancelUrl
    };
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/${jobId}/checkout-session`, payload).pipe(
      map(res => ({
        sessionId: res.data?.sessionId || res.data?.SessionId || null,
        checkoutUrl: res.data?.checkoutUrl || res.data?.CheckoutUrl || res.data?.Url || res.data?.url || null,
        requiresPayment: res.data?.requiresPayment,
        paymentSkipped: res.data?.paymentSkipped
      } as CheckoutSessionResponse))
    );
  }

  verifyPaymentStatus(jobId: number, sessionId: string): Observable<{ success: boolean; isPaid: boolean; transactionId?: string; downloadUrl?: string }> {
    const payload = { sessionId };
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/${jobId}/verify-payment`, payload).pipe(
      map(res => ({
        success: res.data?.success || false,
        isPaid: res.data?.isPaid || false,
        transactionId: res.data?.transactionId || undefined,
        downloadUrl: res.data?.downloadUrl || res.data?.DownloadUrl || undefined
      }))
    );
  }

  getDownloadCheckoutSession(jobId: number, companyId: number, successUrl?: string, cancelUrl?: string): Observable<DownloadCheckoutSessionResponse> {
    const params = new HttpParams().set('companyId', companyId.toString());
    const payload = { successUrl, cancelUrl };
    return this.http.post<ApiResponse<any>>(`${this.baseUrl}/${jobId}/download-checkout-session`, payload, { params }).pipe(
      map(res => ({
        success: res.success,
        message: res.message,
        data: res.data ? {
          requiresPayment: res.data.requiresPayment,
          paymentSkipped: res.data.paymentSkipped,
          checkoutUrl: res.data.checkoutUrl || res.data.CheckoutUrl || res.data.Url || res.data.url,
          downloadUrl: res.data.downloadUrl || res.data.DownloadUrl,
          jobPaymentId: res.data.jobPaymentId || res.data.JobPaymentId,
          remainingAmount: res.data.remainingAmount || res.data.RemainingAmount,
          sessionId: res.data.sessionId || res.data.SessionId
        } : null
      } as DownloadCheckoutSessionResponse))
    );
  }
}

